<template>
  <div class="modal-overlay" v-if="isVisible" @click.self="close">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title"> {{ $t('bidModal.title', { type: resolvedUserType }) }}</h4>
          <button type="button" class="btn-close" @click="close" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p>{{ $t('bidModal.description', { type: resolvedUserType }) }}</p>
          <p>{{ $t('bidModal.avoid_unreasonable') }}</p>

          <form @submit.prevent="submitBid">
            <div class="input-group mb-3">
              <input type="number" v-model="bidAmount" :placeholder="$t('bidModal.price_placeholder')" class="form-control" required />
              <span class="input-group-text">{{ $t('bidModal.currency') }}</span>
            </div>

            <div class="mb-3">
              <input type="number" v-model="phoneNumber" :placeholder="$t('bidModal.phone_placeholder')" class="form-control"
                required :hidden="!!currentUser" />
            </div>

            <div class="mb-3">
              <textarea v-model="description" :placeholder="$t('bidModal.desc_placeholder')" class="form-control" rows="3"></textarea>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-primary" @click="submitBid" :disabled="isSubmitting">
            {{ isSubmitting ? $t('bidModal.submitting') : $t('bidModal.submit') }}
          </button>
          <button type="button" class="btn btn-outline-primary" @click="close">{{ $t('bidModal.close') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const { t } = useI18n()
const props = defineProps({
  product: { type: Object, required: true },
  userTypeText: { type: String, default: '' },
  productUserId: { type: [String, Number], default: null }
})
const emit = defineEmits(['close'])

const isVisible = ref(true)
const bidAmount = ref('')
const phoneNumber = ref('')
const description = ref('')
const isSubmitting = ref(false)

const auth = useAuth()
const currentUser = computed(() => auth.user?.value || null)
const lastPrice = computed(() => props.product?.price?.price || props.product?.price || 0)
const userId = computed(() => currentUser.value?.id || null)
const resolvedUserType = computed(() => props.userTypeText || t('bidModal.seller'))

onMounted(() => {
  if (currentUser.value?.mobile) {
    phoneNumber.value = currentUser.value.mobile
  }
})

const close = () => {
  isVisible.value = false
  emit('close')
}

const validateMobile = (mobile) => {
  return mobile && mobile.length === 11 && mobile.substring(0, 2) === '09'
}

const validateBid = (bid, price) => {
  const minBid = price - (price * 0.30)
  return bid && bid !== '0' && Number(bid) >= minBid
}

const toast = useToast()

const submitBid = async () => {
  if (!validateMobile(phoneNumber.value)) {
    toast.add({
      title: `${t('bidModal.invalid_phone')}`,
      description: '',
      color: 'red',
      icon: 'i-heroicons-exclamation-circle',
      timeout: 5000,
    })
    return
  }

  if (!validateBid(bidAmount.value, lastPrice.value)) {
    toast.add({
      title: `${t('bidModal.invalid_bid')}`,
      description: '',
      color: 'red',
      icon: 'i-heroicons-exclamation-circle',
      timeout: 5000,
    })
    return
  }

  isSubmitting.value = true
  try {
    const bidService = useBidService()
    const response = await bidService.submitBid({
      productId: props.product.id,
      userId: userId.value,
      productUserId: props.productUserId,
      mobile: phoneNumber.value,
      bid: bidAmount.value,
      description: description.value,
      lastPrice: lastPrice.value
    })

    if (response.status === false) {
      toast.warning(response.message || t('bidModal.duplicate'), { timeout: 4000 })
    } else {
      toast.success(response.message || t('bidModal.success'), { timeout: 4000 })
    }
    close()
  } catch (error) {
    toast.add({
      title: `${error.data?.statusMessage || error.message || t('bidModal.error')}`,
      description: '',
      color: 'red',
      icon: 'i-heroicons-exclamation-circle',
      timeout: 5000,
    })
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.modal-dialog {
  width: 100%;
  max-width: 500px;
  margin: auto;
}

.modal-content {
  background: #fff;
  border-radius: $r-lg;
  overflow: hidden;
  box-shadow: $shadow-warm-lg;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid $line;

  .modal-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: $ink;
  }

  .btn-close {
    background: transparent;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.2s;
    &:hover { opacity: 1; }
  }
}

.modal-body {
  padding: 20px;
  font-size: 0.88rem;
  line-height: 1.8;
  color: $ink;

  p {
    margin: 0 0 10px;
    font-size: 0.85rem;
  }

  .form-control {
    border-radius: $r-sm;
    border: 1px solid #ddd;
    padding: 10px 15px;
    font-family: inherit;
    width: 100%;

    &:focus {
      border-color: $leaf;
      box-shadow: 0 0 0 0.2rem rgba(3, 160, 84, 0.25);
      outline: none;
    }

    &[hidden] { display: none; }
  }

  .input-group {
    display: flex;
    align-items: stretch;

    .form-control {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }

    .input-group-text {
      display: flex;
      align-items: center;
      padding: 0 14px;
      background: #f8f9fa;
      border: 1px solid #ddd;
      border-right: none;
      border-radius: 0 $r-sm $r-sm 0;
      font-size: 0.84rem;
      color: $ink-soft;
      white-space: nowrap;
    }
  }

  .mb-3 { margin-bottom: 14px; }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px;
  border-top: 1px solid $line;

  .btn {
    padding: 8px 18px;
    border-radius: $r-pill;
    font-size: 0.84rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: all 0.25s;
    border: 1.5px solid;

    &.btn-primary {
      background: $leaf;
      border-color: $leaf;
      color: #fff;

      &:hover { background: $leaf-deep; border-color: $leaf-deep; }
      &:active { transform: scale(0.97); }
      &:disabled { opacity: 0.6; cursor: not-allowed; }
    }

    &.btn-outline-primary {
      background: transparent;
      border-color: $leaf;
      color: $leaf;

      &:hover { background: $leaf; color: #fff; }
      &:active { transform: scale(0.97); }
    }
  }
}
</style>